import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Box, Button, FormLabel, Stack } from '@mui/material';

import MarkdownEditor from '~/components/Common/MarkdownEditor';

import OffersService from '~/services/OffersService';

import type { ExtraGuestGeneratedPolicies, IncludedGuestEntry } from '~/types/roomPolicy';

import parseRoomPolicyDescription from '~/utils/parseRoomPolicyDescription';
import { reportError } from '~/utils/reportError';
import { capitalise } from '~/utils/stringUtils';

import type { OfferBuilderRoom } from '../types';

export interface SyncRoomPolicyParams {
  dealOfferId: string;
  propertyId: string;
  roomRateId: string;
  roomRateIds?: Array<string>;
  roomTypeId: string;
}

interface Props {
  onChange: (nextRoomValues: Partial<OfferBuilderRoom>) => void;
  room: OfferBuilderRoom;
  syncRoomPolicyParams?: SyncRoomPolicyParams;
}

export default function PoliciesTab({ onChange, room, syncRoomPolicyParams }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [isSyncing, setIsSyncing] = useState(false);
  const policyDescription = room.room_policies ?? '';
  const editorValue = formatPolicyDescription(policyDescription);

  const handleSyncRoomPolicy = useCallback(async () => {
    if (!syncRoomPolicyParams) {
      enqueueSnackbar('Unable to sync room policy because this room is not linked to an offer package', {
        variant: 'warning',
      });
      return;
    }

    setIsSyncing(true);
    try {
      const response = await OffersService.generateRoomPolicy(
        syncRoomPolicyParams.dealOfferId,
        syncRoomPolicyParams.propertyId,
        syncRoomPolicyParams.roomTypeId,
        syncRoomPolicyParams.roomRateId,
        syncRoomPolicyParams.roomRateIds,
      );

      onChange({ room_policies: formatPolicyDescription(response.result.content) });
      enqueueSnackbar('Room policy synced successfully', { variant: 'success' });
    } catch (error) {
      reportError(error);
      enqueueSnackbar('Failed to sync room policy', { variant: 'error' });
    } finally {
      setIsSyncing(false);
    }
  }, [enqueueSnackbar, onChange, syncRoomPolicyParams]);

  return (
    <Stack spacing={2}>
      <FormLabel>Room policies</FormLabel>
      <Box sx={{ '& .rdw-editor-main': { minHeight: 220 } }}>
        <MarkdownEditor
          value={editorValue}
          placeholder="Add room policies, occupancy, check-in details, and restrictions."
          wrapperId={`offer-builder-room-policies-${room.id || room.name || 'room'}`}
          onBlur={(policies) => onChange({ room_policies: policies })}
        />
      </Box>
      <Box>
        <Button variant="contained" startIcon={<AutoFixHighIcon />} onClick={handleSyncRoomPolicy} disabled={isSyncing}>
          {isSyncing ? 'Syncing...' : 'Sync Room Policy'}
        </Button>
      </Box>
    </Stack>
  );
}

function formatPolicyDescription(content: unknown) {
  if (typeof content === 'string') {
    const parsedPolicy = parseRoomPolicyDescription(content);

    if (!parsedPolicy.isStructured) {
      return content;
    }

    return formatStructuredPolicy(parsedPolicy.data);
  }

  if (!content || typeof content !== 'object') {
    return '';
  }

  return formatStructuredPolicy(content as ExtraGuestGeneratedPolicies);
}

function formatStructuredPolicy(policy: ExtraGuestGeneratedPolicies) {
  return [
    formatPackageInclusions(policy.packageInclusions),
    formatMaximumRoomCapacity(policy.maximumRoomCapacities),
    formatBedding(policy),
    ...(policy.additionalPolicyNotes ?? []),
  ]
    .filter(Boolean)
    .join('\n\n');
}

function formatPackageInclusions(items?: ExtraGuestGeneratedPolicies['packageInclusions']) {
  const inclusions = items?.map(formatGuestCounts).filter(Boolean) ?? [];

  if (inclusions.length === 0) {
    return undefined;
  }

  return inclusions.length === 1
    ? `Inclusions valid for ${inclusions[0]}.`
    : `Inclusions valid for:\n${inclusions.map((item) => `- ${item}`).join('\n')}`;
}

function formatMaximumRoomCapacity(items?: ExtraGuestGeneratedPolicies['maximumRoomCapacities']) {
  const capacities = items?.map(formatGuestCounts).filter(Boolean) ?? [];

  if (capacities.length === 0) {
    return undefined;
  }

  return capacities.length === 1
    ? `**Maximum Room Capacity:** ${capitalise(capacities[0])}.`
    : `**Maximum Room Capacity:**\n${capacities.map((item) => `- ${capitalise(item)}`).join('\n')}`;
}

function formatBedding(policy: ExtraGuestGeneratedPolicies) {
  const bedding = [policy.beddingConfiguration, ...(policy.beddingConfigurationExtras ?? [])]
    .map((item) => item?.bedding)
    .filter(Boolean);

  if (bedding.length === 0) {
    return undefined;
  }

  return `**Bedding:** Standard bedding includes ${bedding.join(', ')}.`;
}

function formatGuestCounts(item: IncludedGuestEntry) {
  return [
    formatGuestCount(item.adults, 'adult', 'adults', '16+'),
    formatGuestCount(item.children, 'child', 'children'),
    formatGuestCount(item.teenagers, 'teenager', 'teenagers'),
    formatGuestCount(item.infants, 'infant', 'infants'),
  ]
    .filter(Boolean)
    .join(', ');
}

function formatGuestCount(count: number | undefined, singular: string, plural: string, suffix?: string) {
  if (!count) {
    return undefined;
  }

  return `${count} ${count === 1 ? singular : plural}${suffix ? ` (${suffix})` : ''}`;
}
